/**
 * Records which page the trainer is on (migration 0072).
 *
 * Mounted inside <Router> so it sees every navigation, including the hash
 * routes the app uses for most of the product.
 *
 * WHY IT REPORTS NOTHING WHEN SIGNED OUT
 *   page_views.account_id is NOT NULL and the endpoint requires auth. Logged-out
 *   traffic is mostly /login and /signup, which the funnel already covers, and
 *   an anonymous bucket would need its own identity story to be worth anything.
 *
 * Failures are swallowed. A page must never fail to render, or feel slower,
 * because a telemetry write did not land.
 */
import { useEffect } from "react";
import { useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/components/AuthProvider";
import { apiRequestRaw } from "@/lib/queryClient";
import { normalisePath, shouldRecordView } from "@/lib/pageRoutes";

/** 'ios' | 'android' from the native shell, 'web' from a browser (migration 0101). */
export function viewPlatform(): "ios" | "android" | "web" {
  try {
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : "web";
  } catch {
    return "web";
  }
}

export function PageViewReporter(): null {
  const [location] = useLocation();
  const { account } = useAuth();
  const accountId = account?.id ?? null;

  useEffect(() => {
    if (accountId == null) return;
    const page = normalisePath(location);
    // Throttled per page: a redirect or a re-render should not out-rank a page
    // someone actually sat on.
    if (!shouldRecordView(page)) return;

    void apiRequestRaw("POST", "/api/page-views", { page, platform: viewPlatform() }).catch(() => {
      /* telemetry is never worth surfacing */
    });
  }, [location, accountId]);

  return null;
}
